/**
 * / — Maldek homepage · Clinical Thinking Improvement System (2026).
 * Ultra-short narrative: HERO → THE GAP → CORE EXPERIENCE → HOW IT TEACHES → FINAL CTA.
 * Imagery: Black nursing students (AI master-style + real stock photography).
 * Palette: warm ivory / espresso / copper / subtle burgundy — no hospital blue.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useView } from "../lib/view";
import { getIdentity } from "../lib/learnerIdentity";
import { usePageMeta } from "../lib/seo";
import { StatusBadge } from "../components/StatusBadge";
import DownloadAppButton from "../components/DownloadAppButton";
import { ArrowRight, ChevronDown, Search, Rewind } from "lucide-react";

const BG = "#FAF6EE";
const CARD = "#FFFFFF";
const CARDSOFT = "#FFFDF8";
const STRIP = "#F3ECDD";
const EDGE = "rgba(43,38,28,0.14)";
const INK = "#2B261C";
const ESPRESSO = "#241F17";
const SUB = "#5D5748";
const MUTE = "#8A8271";
const COPPER = "#A5673F";
const BURGUNDY = "#6B8F71";
const GREEN = "#4C7A54";
const ONACCENT = "#FFFDFA";
const CREAM = "#EAD9BE";
const SERIF = '"Fraunces", Georgia, "Times New Roman", serif';
const BODY = '"IBM Plex Sans", system-ui, sans-serif';

const R = {
  reasoning: "/clinical-reasoning",
  timeMachine: "/time-machine",
  howItWorks: "/how-it-works",
  educators: "/for-schools",
  community: "/community",
  about: "/concept",
  login: "/login",
  pricing: "/pricing",
};

const ease = [0.22, 1, 0.36, 1];
const rise = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease, delay: i * 0.06 } }),
};
const focusRing = "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#A5673F]";

function Wrap({ children, className = "", id }) {
  return <section id={id} className={`px-6 sm:px-10 lg:px-16 ${className}`}><div className="mx-auto max-w-[1180px]">{children}</div></section>;
}
function Kicker({ children, tone = COPPER }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="h-px w-8" style={{ background: tone }} />
      <span className="text-[11px] font-bold uppercase tracking-[0.28em]" style={{ color: tone, fontFamily: BODY }}>{children}</span>
    </div>
  );
}
function H2({ children }) {
  return (
    <h2 className="tracking-tight text-[28px] sm:text-[40px] leading-[1.1] font-semibold" style={{ color: INK, fontFamily: SERIF }}>
      {children}
    </h2>
  );
}

/* ── NAV ───────────────────────────────────────────────────────── */
const PRIMARY_NAV = [
  ["Clinical Reasoning", R.reasoning],
  ["Time Machine", R.timeMachine],
  ["How It Works", R.howItWorks],
  ["For Educators", R.educators],
];
const MORE_NAV = [
  { to: R.community, label: "Community Health", status: null },
  { to: "/modules", label: "All Modules", status: null },
  { to: "/schools", label: "Maldek Schools", status: null },
  { to: "/bls-academy", label: "BLS/ACLS Academy", status: null },
  { to: R.about, label: "About Maldek", status: null },
];

function Nav({ view, setView }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ident, setIdent] = useState(() => getIdentity());
  const [linkedId, setLinkedId] = useState(() => localStorage.getItem("mc_learner_id") || "");
  useEffect(() => {
    const sync = () => {
      setIdent(getIdentity());
      setLinkedId(localStorage.getItem("mc_learner_id") || "");
    };
    window.addEventListener("storage", sync);
    window.addEventListener("maldek-identity-change", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("maldek-identity-change", sync);
    };
  }, []);
  const firstName = ident?.display_name?.split(" ")[0] || "";
  return (
    <header className="sticky top-0 z-50" style={{ background: "rgba(250,246,238,0.92)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${EDGE}` }} data-testid="global-nav">
      <div className="mx-auto max-w-[1180px] px-4 sm:px-10 lg:px-16 h-16 flex items-center justify-between gap-2">
        <Link to="/" className={`flex items-center gap-2.5 shrink-0 ${focusRing} rounded-lg`} data-testid="nav-logo" aria-label="Maldek home">
          <img src="/brand/icon.png" alt="Maldek — Tree of Knowledge" className="h-8 w-8 rounded-xl object-cover shrink-0" style={{ boxShadow: "0 6px 16px rgba(43,38,28,0.25)" }} data-testid="home-brand-logo" />
          <span className="text-[16px] sm:text-[17px] font-semibold tracking-tight whitespace-nowrap" style={{ color: INK, fontFamily: SERIF }}>Maldek <span style={{ color: COPPER, fontStyle: "italic" }}>by Hill</span></span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 mx-4" style={{ fontFamily: BODY }} aria-label="Primary">
          {PRIMARY_NAV.map(([l, t], i) => (
            <Link key={l} to={t} className={`text-[13.5px] whitespace-nowrap transition-colors ${focusRing} rounded ${i === 0 ? "font-bold" : "font-medium"}`}
              style={{ color: i === 0 ? BURGUNDY : SUB }}
              onMouseEnter={(e) => (e.currentTarget.style.color = BURGUNDY)}
              onMouseLeave={(e) => (e.currentTarget.style.color = i === 0 ? BURGUNDY : SUB)}>
              {l}
            </Link>
          ))}
          <div className="relative" onMouseLeave={() => setMoreOpen(false)}>
            <button className={`flex items-center gap-1 text-[13.5px] font-medium whitespace-nowrap transition-colors ${focusRing} rounded`} style={{ color: SUB }} data-testid="nav-explore-more" aria-haspopup="true" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
              More <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {moreOpen && (
              <div className="absolute right-0 top-full pt-2 w-72" data-testid="nav-explore-menu">
                <div className="rounded-2xl p-2" style={{ background: CARD, border: `1px solid ${EDGE}`, boxShadow: "0 18px 40px rgba(43,38,28,0.14)" }}>
                  {MORE_NAV.map((m) => (
                    <Link key={m.label} to={m.to} className="flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors hover:bg-[#F5EFE3]" onClick={() => setMoreOpen(false)}>
                      <span className="text-[13.5px] font-semibold" style={{ color: INK, fontFamily: BODY }}>{m.label}</span>
                      {m.status && <StatusBadge status={m.status} size="sm" showDot={false} />}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden md:flex items-center p-1 rounded-full" style={{ background: STRIP, border: `1px solid ${EDGE}` }} data-testid="view-toggle" role="group" aria-label="Learner view">
            {[["nursing", "Nursing"], ["np", "NP"]].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} className={`px-3 py-1.5 rounded-full text-[12px] font-bold transition-colors ${focusRing}`}
                style={view === id ? { background: BURGUNDY, color: ONACCENT, fontFamily: BODY } : { color: SUB, fontFamily: BODY }}
                data-testid={`view-toggle-${id}`} aria-pressed={view === id}>
                {label}
              </button>
            ))}
          </div>
          {firstName ? (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: SUB, fontFamily: BODY }} data-testid="nav-greeting">
              Hi, {firstName}{linkedId ? " ✓" : ""}
            </span>
          ) : (
            <Link to={R.login} className={`hidden sm:inline-flex whitespace-nowrap text-[13px] font-semibold px-3 py-2 rounded-full transition-colors ${focusRing}`} style={{ color: INK, fontFamily: BODY }} data-testid="nav-login">Log In</Link>
          )}
          <Link to={R.reasoning} data-testid="nav-cta" className={`inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-2 text-[12px] sm:text-[13px] font-bold whitespace-nowrap transition-transform hover:-translate-y-0.5 ${focusRing}`} style={{ background: BURGUNDY, color: ONACCENT, fontFamily: BODY, boxShadow: "0 8px 20px rgba(107,143,113,0.3)" }}>
            Start Learning
          </Link>
          <button className={`xl:hidden p-2 rounded-lg ${focusRing}`} onClick={() => setMobileOpen((o) => !o)} aria-label="Toggle menu" aria-expanded={mobileOpen} data-testid="nav-mobile-toggle">
            <span className="block w-5 h-0.5 mb-1" style={{ background: INK }} /><span className="block w-5 h-0.5 mb-1" style={{ background: INK }} /><span className="block w-3.5 h-0.5" style={{ background: INK }} />
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="xl:hidden px-6 pb-4" data-testid="nav-mobile-menu" style={{ borderTop: `1px solid ${EDGE}` }}>
          <nav className="flex flex-col">
            {firstName && (
              <p className="pt-3 pb-1 text-[13px] font-bold flex items-center gap-1.5" style={{ color: INK, fontFamily: BODY }} data-testid="nav-mobile-greeting">
                Hi, {firstName}{linkedId ? " ✓" : ""}
              </p>
            )}
            {PRIMARY_NAV.map(([l, t]) => (
              <Link key={l} to={t} onClick={() => setMobileOpen(false)} className={`py-2.5 text-[15px] font-semibold ${focusRing} rounded`} style={{ color: INK, fontFamily: BODY }}>{l}</Link>
            ))}
            <div className="mt-1 pt-2" style={{ borderTop: `1px solid ${EDGE}` }}>
              {MORE_NAV.map((m) => (
                <Link key={m.label} to={m.to} onClick={() => setMobileOpen(false)} className="flex items-center justify-between py-2.5">
                  <span className="text-[14px] font-medium" style={{ color: SUB, fontFamily: BODY }}>{m.label}</span>
                  {m.status && <StatusBadge status={m.status} size="sm" showDot={false} />}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

/* ── SECTION 1 · HERO ──────────────────────────────────────────── */
function Hero() {
  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 h-[440px] w-[440px] rounded-full opacity-40 blur-[110px]" style={{ background: CREAM }} aria-hidden="true" />
      <div className="pointer-events-none absolute top-48 -left-32 h-[380px] w-[380px] rounded-full opacity-30 blur-[110px]" style={{ background: "#E7C9C0" }} aria-hidden="true" />
      <Wrap className="pt-12 sm:pt-16 pb-12 sm:pb-16">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
          <div>
            <motion.div variants={rise} initial="hidden" animate="show">
              <Kicker>Maldek by Hill · Clinical Thinking Improvement System</Kicker>
            </motion.div>
            <motion.h1 variants={rise} initial="hidden" animate="show" custom={1}
              className="tracking-tight leading-[1.06] text-[36px] sm:text-[50px] lg:text-[54px] font-semibold" style={{ color: INK, fontFamily: SERIF }} data-testid="hero-headline">
              Learn the disease. See the patient.{" "}
              <span style={{ color: BURGUNDY, fontStyle: "italic" }}>Think through what happens next.</span>
            </motion.h1>
            <motion.p variants={rise} initial="hidden" animate="show" custom={2}
              className="mt-5 max-w-xl text-[16px] sm:text-[17px] leading-relaxed" style={{ color: SUB, fontFamily: BODY }}>
              Maldek connects pathophysiology, bedside findings, clinical decisions, treatment, and outcomes into one reasoning experience — across 470+ conditions.
            </motion.p>
            <motion.div variants={rise} initial="hidden" animate="show" custom={3} className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to={R.reasoning} data-testid="hero-cta-primary" className={`group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-bold transition-transform hover:-translate-y-0.5 ${focusRing}`} style={{ background: BURGUNDY, color: ONACCENT, fontFamily: BODY, boxShadow: "0 12px 28px rgba(107,143,113,0.32)" }}>
                Start Clinical Reasoning <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to={R.howItWorks} data-testid="hero-cta-secondary" className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-bold transition-colors ${focusRing}`} style={{ color: INK, background: CARD, border: `1.5px solid ${EDGE}`, fontFamily: BODY }}>
                See How It Works
              </Link>
            </motion.div>
            <motion.p variants={rise} initial="hidden" animate="show" custom={4} className="mt-5 text-[13px] font-medium" style={{ color: MUTE, fontFamily: BODY }} data-testid="hero-trust-line">
              Built for nursing students, advanced practice learners, educators, and healthcare organizations.
            </motion.p>
          </div>
          <motion.div variants={rise} initial="hidden" animate="show" custom={3}>
            <div className="relative rounded-3xl overflow-hidden" style={{ border: `1px solid ${EDGE}`, boxShadow: "0 28px 70px rgba(43,38,28,0.22)" }} data-testid="hero-visual">
              <img src="/img/audience/student.jpeg" alt="A Black nursing student working through her cardiovascular notes in the library — the future is nursing" className="w-full h-auto block" loading="eager" />
              <span className="absolute top-4 left-4 rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em]" style={{ background: "rgba(36,31,23,0.6)", color: CREAM, fontFamily: BODY, backdropFilter: "blur(4px)" }} data-testid="hero-visual-badge">
                Built for the next generation of nurses
              </span>
            </div>
          </motion.div>
        </div>
      </Wrap>
    </div>
  );
}

/* ── SECTION 2 · THE GAP ───────────────────────────────────────── */
const GAP_COLS = [
  { title: "They memorize", tone: MUTE, items: ["Symptoms", "Labs", "Medications", "Pathophysiology"] },
  { title: "But the patient arrives all at once", tone: BURGUNDY, items: ["Shortness of breath", "Abnormal vitals", "Conflicting clues", "Time pressure"] },
  { title: "Clinical reasoning connects them", tone: GREEN, items: ["What is happening?", "Why is it happening?", "What matters first?", "What should I do next?"] },
];

function GapSection() {
  return (
    <div style={{ background: STRIP, borderTop: `1px solid ${EDGE}`, borderBottom: `1px solid ${EDGE}` }}>
      <Wrap className="py-12 sm:py-14" id="the-gap">
        <div className="grid lg:grid-cols-[0.85fr_1.65fr] gap-8 items-center">
          <div>
            <Kicker>The gap</Kicker>
            <h2 className="tracking-tight text-[26px] sm:text-[30px] leading-[1.12] font-semibold" style={{ color: INK, fontFamily: SERIF }}>
              Students learn the pieces.<br /><span style={{ color: COPPER, fontStyle: "italic" }}>Maldek teaches the connections.</span>
            </h2>
            <p className="mt-3 text-[15px] font-semibold m-0" style={{ color: INK, fontFamily: SERIF }} data-testid="gap-closer">
              Maldek turns isolated facts into a clinical story.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3" data-testid="gap-columns">
            {GAP_COLS.map((c, ci) => (
              <div key={c.title} className="rounded-2xl p-4" style={{ background: CARD, border: `1px solid ${EDGE}` }} data-testid={`gap-col-${ci}`}>
                <h3 className="text-[14px] font-semibold leading-snug" style={{ color: c.tone === MUTE ? INK : c.tone, fontFamily: SERIF }}>{c.title}</h3>
                <ul className="mt-2.5 space-y-1.5">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-center gap-2 text-[12.5px]" style={{ color: SUB, fontFamily: BODY }}>
                      <span className="h-1 w-1 rounded-full flex-shrink-0" style={{ background: c.tone }} aria-hidden />{it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </Wrap>
    </div>
  );
}

/* ── SECTION 3 · THE CORE EXPERIENCE ───────────────────────────── */
const FEATURED = [
  { slug: "diabetic-ketoacidosis", label: "Diabetic Ketoacidosis" },
  { slug: "heart-failure-exacerbation", label: "Heart Failure" },
  { slug: "sepsis", label: "Sepsis" },
  { slug: "acute-ischemic-stroke", label: "Stroke" },
  { slug: "pulmonary-embolism", label: "Pulmonary Embolism" },
  { slug: "upper-gi-bleed", label: "Upper GI Bleed" },
];

function CoreExperience() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(FEATURED[0].slug);
  const [q, setQ] = useState("");
  useEffect(() => {
    let alive = true;
    fetch("/cr-catalog-seed.json")
      .then((r) => r.json())
      .then((d) => { if (alive) setCatalog(d.conditions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const bySlug = useMemo(() => Object.fromEntries(catalog.map((c) => [c.slug, c])), [catalog]);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return catalog.filter((c) => (c.title || "").toLowerCase().includes(t) || (c.shortName || "").toLowerCase().includes(t)).slice(0, 7);
  }, [q, catalog]);
  const cond = bySlug[selected];
  return (
    <Wrap className="py-12 sm:py-16" id="clinical-reasoning">
      <Kicker>The core experience</Kicker>
      <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 items-start">
        <div>
          <H2>Start with a condition. <span style={{ color: COPPER, fontStyle: "italic" }}>Build the reasoning.</span></H2>
          <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed" style={{ color: SUB, fontFamily: BODY }}>
            Choose a condition and work through the disease from normal physiology to the first break, compensation, manifestations, differential diagnosis, treatment, reassessment, and prevention.
          </p>
          <div className="mt-7 flex flex-wrap gap-2" data-testid="condition-selector">
            {FEATURED.map((f) => (
              <button key={f.slug} type="button" onClick={() => setSelected(f.slug)}
                className={`rounded-full px-4 py-2 text-[13px] font-bold transition-colors ${focusRing}`}
                style={selected === f.slug
                  ? { background: BURGUNDY, color: ONACCENT, fontFamily: BODY }
                  : { background: CARD, color: SUB, border: `1px solid ${EDGE}`, fontFamily: BODY }}
                data-testid={`condition-chip-${f.slug}`} aria-pressed={selected === f.slug}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="mt-4 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: MUTE }} aria-hidden />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && matches[0]) navigate(`/clinical-reasoning/condition/${matches[0].slug}`); }}
              placeholder={`Search any of ${catalog.length || 470}+ conditions`}
              className={`w-full rounded-full pl-11 pr-4 py-3 text-[14px] ${focusRing}`}
              style={{ background: CARD, border: `1px solid ${EDGE}`, color: INK, fontFamily: BODY }}
              data-testid="condition-search-input"
              aria-label="Search any condition"
            />
            {matches.length > 0 && (
              <div className="absolute z-20 left-0 right-0 top-full mt-2 rounded-2xl p-1.5 max-h-72 overflow-y-auto" style={{ background: CARD, border: `1px solid ${EDGE}`, boxShadow: "0 18px 40px rgba(43,38,28,0.14)" }} data-testid="condition-search-results">
                {matches.map((m) => (
                  <button key={m.slug} type="button"
                    onClick={() => { setSelected(m.slug); setQ(""); }}
                    className="w-full text-left px-3.5 py-2.5 rounded-xl transition-colors hover:bg-[#F5EFE3]"
                    data-testid={`condition-search-result-${m.slug}`}>
                    <span className="text-[13.5px] font-bold block" style={{ color: INK, fontFamily: BODY }}>{m.title}</span>
                    <span className="text-[11px] uppercase tracking-[0.14em] font-mono" style={{ color: MUTE }}>{m.category}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <motion.div variants={rise} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="rounded-3xl overflow-hidden" style={{ background: CARD, border: `1px solid ${EDGE}`, boxShadow: "0 20px 50px rgba(43,38,28,0.1)" }} data-testid="condition-preview-card">
          <img src={`/condition-art/${selected}.webp`} alt={`${cond?.title || "Condition"} — normal anatomy`} className="w-full h-44 sm:h-52 object-cover block" loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] m-0" style={{ color: COPPER }}>{cond?.category || "Clinical Reasoning"}</p>
            <h3 className="mt-1.5 text-[22px] font-semibold leading-tight" style={{ color: INK, fontFamily: SERIF }} data-testid="condition-preview-title">{cond?.title || "Diabetic Ketoacidosis"}</h3>
            {cond?.oneLineTruth && (
              <p className="mt-2.5 text-[13.5px] leading-relaxed" style={{ color: SUB, fontFamily: BODY }} data-testid="condition-preview-truth">{cond.oneLineTruth}</p>
            )}
            <Link to={`/clinical-reasoning/condition/${selected}`} data-testid="condition-preview-open"
              className={`mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13.5px] font-bold transition-transform hover:-translate-y-0.5 ${focusRing}`}
              style={{ background: BURGUNDY, color: ONACCENT, fontFamily: BODY }}>
              Reason through this condition <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </Wrap>
  );
}

/* ── SECTION 4 · HOW IT TEACHES ────────────────────────────────── */
const MALDEK = [
  { l: "M", t: "Map the Story" },
  { l: "A", t: "Assess Normal" },
  { l: "L", t: "Locate the First Break" },
  { l: "D", t: "Decode Compensation" },
  { l: "E", t: "Evaluate Manifestations & Look-Alikes" },
  { l: "K", t: "Key In Treatment, Recovery & Prevention" },
];

function HowItTeaches() {
  return (
    <div style={{ background: STRIP, borderTop: `1px solid ${EDGE}`, borderBottom: `1px solid ${EDGE}` }}>
      <Wrap className="py-12 sm:py-16" id="how-it-teaches">
        <div className="grid lg:grid-cols-[1fr_0.95fr] gap-10 items-center">
          <div>
            <Kicker>How it teaches</Kicker>
            <H2>One framework. <span style={{ color: COPPER, fontStyle: "italic" }}>MALDEK.</span></H2>
            <div className="mt-6 rounded-3xl px-5 sm:px-6 py-2" style={{ background: CARD, border: `1px solid ${EDGE}` }} data-testid="maldek-steps">
              {MALDEK.map((m, i) => (
                <div key={m.l} className="py-3 flex items-center gap-4" style={i > 0 ? { borderTop: `1px solid rgba(43,38,28,0.08)` } : {}} data-testid={`maldek-step-${m.l}`}>
                  <span className="grid place-items-center h-8 w-8 rounded-full flex-shrink-0 font-extrabold text-[15px]" style={{ background: BURGUNDY, color: ONACCENT, fontFamily: SERIF }} aria-hidden>{m.l}</span>
                  <span className="text-[14.5px] font-semibold" style={{ color: INK, fontFamily: SERIF }}>{m.t}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: SUB, fontFamily: BODY }} data-testid="how-it-teaches-extras">
              Then rewind the disease in the <Link to={R.timeMachine} className={`font-bold underline underline-offset-2 ${focusRing} rounded`} style={{ color: BURGUNDY }} data-testid="how-it-teaches-tm-link"><Rewind className="inline h-3.5 w-3.5 -mt-0.5" aria-hidden /> Time Machine</Link> to see where it truly began — and lock it in with five reflection questions that test causal reasoning, safety, treatment, prevention, and transfer.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4" data-testid="how-it-teaches-photos">
            <figure className="m-0 rounded-3xl overflow-hidden" style={{ border: `1px solid ${EDGE}`, boxShadow: "0 16px 40px rgba(43,38,28,0.14)" }}>
              <img src="/img/audience/educator.jpeg" alt="A Black nurse educator teaching students at a simulation bedside" className="w-full h-full object-cover block" loading="lazy" data-testid="photo-students-practice" />
            </figure>
            <figure className="m-0 rounded-3xl overflow-hidden mt-8" style={{ border: `1px solid ${EDGE}`, boxShadow: "0 16px 40px rgba(43,38,28,0.14)" }}>
              <img src="/img/audience/community.jpeg" alt="A Black community health worker guiding an older couple through their care plan" className="w-full h-full object-cover block" loading="lazy" data-testid="photo-students-case-review" />
            </figure>
            <figcaption className="col-span-2 text-[11.5px] font-medium" style={{ color: MUTE, fontFamily: BODY }}>
              Practice the skill. Reason the case. Explain it back — that is how Maldek locks learning in.
            </figcaption>
          </div>
        </div>
      </Wrap>
    </div>
  );
}

/* ── SECTION 5 · FINAL CTA ─────────────────────────────────────── */
function FinalCta() {
  return (
    <Wrap className="py-14 sm:py-16">
      <div className="rounded-3xl p-9 sm:p-12 text-center" style={{ background: ESPRESSO, boxShadow: "0 24px 60px rgba(36,31,23,0.35)" }}>
        <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] mb-3" style={{ color: CREAM, fontFamily: BODY }} data-testid="final-cta-eyebrow">The patient is the test</p>
        <h2 className="mx-auto max-w-2xl tracking-tight text-[30px] sm:text-[42px] leading-[1.12] font-semibold" style={{ color: ONACCENT, fontFamily: SERIF }}>
          Stop memorizing the disease.{" "}
          <span style={{ fontStyle: "italic", color: CREAM }}>Learn to reason through the patient.</span>
        </h2>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <Link to={R.reasoning} data-testid="final-cta-primary" className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-[15px] font-bold transition-transform hover:-translate-y-0.5 ${focusRing}`} style={{ background: CREAM, color: ESPRESSO, fontFamily: BODY }}>
            Start Clinical Reasoning <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to={R.educators} data-testid="final-cta-educators" className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-[15px] font-bold transition-colors ${focusRing}`} style={{ color: ONACCENT, background: "rgba(250,246,238,0.1)", border: "1.5px solid rgba(250,246,238,0.4)", fontFamily: BODY }}>
            Explore for Educators
          </Link>
        </div>
        <Link to={R.community} data-testid="final-cta-community" className={`mt-5 inline-block text-[13px] font-semibold underline underline-offset-4 ${focusRing} rounded`} style={{ color: "rgba(250,246,238,0.7)", fontFamily: BODY }}>
          Explore Community Health
        </Link>
      </div>
    </Wrap>
  );
}

/* ── FOOTER ────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="px-6 sm:px-10 lg:px-16 py-14" style={{ background: STRIP, borderTop: `1px solid ${EDGE}` }}>
      <div className="mx-auto max-w-[1180px] grid gap-8 lg:grid-cols-[1.5fr_1fr_1fr_1.2fr]">
        <div>
          <div className="flex items-center gap-2.5">
            <img src="/brand/icon.png" alt="Maldek" className="h-8 w-8 rounded-xl object-cover" data-testid="footer-brand-logo" />
            <span className="flex flex-col leading-tight">
              <span className="text-[16px] font-semibold" style={{ color: INK, fontFamily: SERIF }}>Maldek by Hill</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: MUTE, fontFamily: BODY }} data-testid="footer-brand-descriptor">Clinical Thinking Improvement System</span>
            </span>
          </div>
          <p className="mt-3 max-w-xs text-[13px] leading-relaxed" style={{ color: SUB, fontFamily: BODY }}>
            Learn the disease. See the patient. Think through what happens next.
          </p>
          <div className="mt-4">
            <DownloadAppButton className={focusRing} style={{ color: INK, background: CARD, border: `1.5px solid ${COPPER}55`, fontFamily: BODY }} />
          </div>
        </div>
        <div>
          <div className="text-[12px] font-bold uppercase tracking-[0.14em] mb-3" style={{ color: INK, fontFamily: BODY }}>Learn</div>
          <ul className="space-y-2">
            {[["Clinical Reasoning", R.reasoning], ["Time Machine", R.timeMachine], ["How It Works", R.howItWorks], ["For Educators", R.educators], ["All Modules", "/modules"], ["Pricing", R.pricing]].map(([l, t]) => (
              <li key={l}><Link to={t} className="text-[13px]" style={{ color: SUB, fontFamily: BODY }} onMouseEnter={(e) => (e.currentTarget.style.color = COPPER)} onMouseLeave={(e) => (e.currentTarget.style.color = SUB)}>{l}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[12px] font-bold uppercase tracking-[0.14em] mb-3" style={{ color: INK, fontFamily: BODY }}>Also on Maldek</div>
          <ul className="space-y-2.5">
            <li className="flex items-center gap-2"><Link to={R.community} className="text-[13px]" style={{ color: SUB, fontFamily: BODY }}>Community Health</Link><StatusBadge status="available" size="sm" showDot={false} /></li>
            <li className="flex items-center gap-2"><Link to="/bls-academy" className="text-[13px]" style={{ color: SUB, fontFamily: BODY }}>BLS/ACLS Academy</Link><StatusBadge status="available" size="sm" showDot={false} /></li>
          </ul>
        </div>
        <div>
          <div className="text-[12px] font-bold uppercase tracking-[0.14em] mb-3" style={{ color: INK, fontFamily: BODY }}>Legal &amp; Contact</div>
          <ul className="space-y-2">
            {[["Privacy", "/privacy"], ["Terms", "/terms"], ["About", R.about]].map(([l, t]) => (
              <li key={l}><Link to={t} className="text-[13px]" style={{ color: SUB, fontFamily: BODY }}>{l}</Link></li>
            ))}
          </ul>
          <p className="mt-4 text-[11.5px] leading-snug" style={{ color: SUB, fontFamily: BODY }}>
            The BLS/ACLS Academy is an original training surface aligned to current AHA Guidelines for CPR &amp; ECC. It is not an official American Heart Association course and does not issue AHA cards.
          </p>
        </div>
      </div>
      <div className="mx-auto max-w-[1180px] mt-10 pt-5 flex flex-col sm:flex-row items-center justify-between gap-2" style={{ borderTop: `1px solid ${EDGE}` }}>
        <span className="text-[12px]" style={{ color: SUB, fontFamily: BODY }}>© {new Date().getFullYear()} Maldek by Hill.</span>
        <span className="text-[11.5px]" style={{ color: SUB, fontFamily: BODY }}>For healthcare education — not a substitute for clinical judgment.</span>
      </div>
    </footer>
  );
}

/* ── PAGE ──────────────────────────────────────────────────────── */
export default function MaldekHome() {
  usePageMeta({
    title: "Maldek by Hill | Clinical Reasoning",
    description: "Maldek is a clinical reasoning learning platform that connects pathophysiology, patient findings, treatment decisions, and outcomes through interactive disease experiences.",
  });
  const [view, setView] = useView();
  return (
    <div data-testid="maldek-home" className="min-h-screen antialiased overflow-x-clip" style={{ background: BG, color: INK, fontFamily: BODY }}>
      <Nav view={view} setView={setView} />
      <Hero />
      <GapSection />
      <CoreExperience />
      <HowItTeaches />
      <FinalCta />
      <Footer />
    </div>
  );
}
